/**
 * Telas de página cheia, sem a navegação do painel (proposta "Elements", 17/10/2026):
 *  - BlockedScreen: status `blocked` (antes "AVISO LEGAL"), com o link dos Termos de Uso;
 *  - InactiveScreen: status `inactive`/`not installed`, com o botão do suporte;
 *  - MaintenanceScreen: MAINTENANCE_MODE (config/env), com "Tentar de novo";
 *  - MobileScreen: celular (até 640 px), com o endereço do painel para copiar e abrir no
 *    computador. O global.css esconde o cabeçalho e o conteúdo nessa largura.
 *
 * Movimento (23/10/2026): botões com a resposta só de cor (.ds-resposta, M1 a); "Tentar de novo"
 * passa ao giro se a página demorar mais de 200 ms para recarregar; "Copiar o link" troca o ícone
 * pelo ✓ e o rótulo por "Link copiado" no mesmo lugar (.ds-troca) e volta sozinho em 2 s.
 */
import { useEffect, useRef, useState } from 'react';
import { useBrand } from '../../hooks/useBrand';
import { assetUrls, legalUrls } from '../../config/env';
import { AuthLayout, RotuloCarregando, StateIcon } from '../screen/Screen';
import { useScreenText } from '../screen/screenText';
import '../screen/screen.css';

/** "Link copiado" fica 2 s (--permanencia-2000: tempo de leitura, igual com reduzir movimento). */
const COPIED_FOR = 2000;

function useLinks() {
  const brand = useBrand();
  return legalUrls[brand.source === 'lead-machine' ? 'lead-machine' : 'eminer'];
}

export function BlockedScreen(): JSX.Element {
  const { tx } = useScreenText();
  const links = useLinks();
  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="block" tone="red" />
        <h1 id="as-title">{tx('blocked_title')}</h1>
        <p className="as-card__text">{tx('blocked_text')}</p>
        <a
          className="as-link as-link--lg"
          href={links.terms}
          target="_blank"
          rel="noopener noreferrer"
        >
          {tx('blocked_terms')}
        </a>
      </section>
    </AuthLayout>
  );
}

export function InactiveScreen(): JSX.Element {
  const { tx } = useScreenText();
  const links = useLinks();
  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="power_settings_new" tone="gray" />
        <div className="as-head">
          <h1 id="as-title">{tx('inactive_title')}</h1>
          <p className="as-card__text">{tx('inactive_text')}</p>
        </div>
        <a
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          href={links.support}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            support_agent
          </span>
          {tx('support')}
        </a>
      </section>
    </AuthLayout>
  );
}

export function MaintenanceScreen(): JSX.Element {
  const { tx } = useScreenText();
  // Recarregando: um clique só (o segundo não faz nada) e, passados 200 ms, o giro no ícone.
  const [retrying, setRetrying] = useState(false);
  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="construction" />
        <div className="as-head">
          <h1 id="as-title">{tx('maint_title')}</h1>
          <p className="as-card__text">{tx('maint_text')}</p>
        </div>
        <button
          type="button"
          className="as-btn as-btn--outline as-btn--block ds-resposta"
          aria-disabled={retrying || undefined}
          aria-busy={retrying || undefined}
          onClick={() => {
            if (retrying) return;
            setRetrying(true);
            window.location.reload();
          }}
        >
          <RotuloCarregando ocupado={retrying} icone="refresh" rotulo={tx('retry')} />
        </button>
      </section>
    </AuthLayout>
  );
}

export function MobileScreen(): JSX.Element {
  const { tx } = useScreenText();
  const brand = useBrand();
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  const url = typeof window !== 'undefined' ? window.location.origin : '';
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      // Volta sozinho em 2 s (--permanencia-2000); copiar de novo reinicia o tempo.
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_FOR);
    } catch {
      /* sem permissão de área de transferência: o endereço continua à vista para copiar */
    }
  }

  return (
    <div className="as-root as-mobile">
      <div className="as-brand as-brand--left">
        <img src={assetUrls.logoLeadMachine} alt="" width={32} height={32} />
        <strong>{brand.appTitle}</strong>
      </div>
      <section className="as-card" aria-labelledby="as-mobile-title">
        <StateIcon icon="computer" />
        <h1 id="as-mobile-title">{tx('mobile_title')}</h1>
        <p className="as-card__text">{tx('mobile_text')}</p>
        <p className="as-url">
          <span className="material-symbols-rounded" aria-hidden="true">
            link
          </span>
          <span>{url.replace(/^https?:\/\//, '')}</span>
        </p>
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          onClick={() => void copy()}
        >
          <span className="ds-troca" aria-hidden="true">
            <span className={`material-symbols-rounded${copied ? ' ds-fora' : ''}`}>
              content_copy
            </span>
            <span className={`material-symbols-rounded${copied ? '' : ' ds-fora'}`}>check</span>
          </span>
          <span className="ds-troca ds-troca-texto">
            <span className={copied ? 'ds-fora' : undefined} aria-hidden={copied || undefined}>
              {tx('copy')}
            </span>
            <span className={copied ? undefined : 'ds-fora'} aria-hidden={!copied || undefined}>
              {tx('copied')}
            </span>
          </span>
        </button>
        <span className="as-sr" role="status">
          {copied ? tx('copied') : ''}
        </span>
      </section>
    </div>
  );
}
